import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import SignaturePad from "../components/SignaturePad";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Search } from "lucide-react";

export default function PublicAttendance() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [sig, setSig] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/public/attendance/${token}`);
    setData(r.data);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!selected) return toast.error("Selecione seu nome");
    if (!sig) return toast.error("Assine para registrar presença");
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/public/attendance/${token}/check-in`, { participant_id: selected.participant_id, signature_data_url: sig });
      toast.success("Presença registrada com sucesso!");
      await load();
      setSelected(null);
      setSig("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao registrar");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center overline">Carregando…</div>;
  const brand = data.brand || {};
  const primary = brand.brand_primary || "#C07C88";
  const gold = brand.brand_accent || "#D4AF37";
  const closed = data.session.closed;
  const filtered = data.participants.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()) || (p.email || "").toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="doc-paper p-8 md:p-10">
          <div className="text-center pb-6 border-b border-border">
            <p className="overline">Registro de Presença</p>
            <h1 className="font-heading text-3xl mt-2" style={{ color: primary }}>{data.training.title}</h1>
            <p className="text-sm text-muted-foreground mt-2">{data.session.label} · {data.session.hours}h</p>
            {closed && <p className="mt-3 inline-block px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs font-medium">SESSÃO ENCERRADA</p>}
          </div>

          {!selected ? (
            <div className="mt-8">
              <Label className="text-xs uppercase tracking-wider">Encontre seu nome</Label>
              <div className="relative mt-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Buscar por nome ou e-mail" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="search-participant" />
              </div>
              <div className="mt-4 divide-y divide-border max-h-96 overflow-auto border border-border rounded-md">
                {filtered.map((p) => (
                  <button
                    key={p.participant_id}
                    disabled={p.already_signed || closed}
                    onClick={() => setSelected(p)}
                    data-testid={`select-participant-${p.participant_id}`}
                    className={`w-full text-left p-3 flex items-center justify-between hover:bg-[#FAFAFA] disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    <div>
                      <p className="font-medium text-sm">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.email}</p>
                    </div>
                    {p.already_signed ? (
                      <span className="text-xs inline-flex items-center gap-1 text-[#10B981]"><CheckCircle2 size={14} /> Presente</span>
                    ) : (
                      <span className="text-xs text-[#C07C88]">Selecionar →</span>
                    )}
                  </button>
                ))}
                {filtered.length === 0 && <p className="text-xs text-muted-foreground p-6 text-center">Nenhum participante encontrado</p>}
              </div>
            </div>
          ) : (
            <div className="mt-8 space-y-4">
              <div className="doc-paper p-4 bg-[#FDF9EE]/40">
                <p className="overline">Confirmando presença de</p>
                <p className="font-heading text-2xl mt-1" style={{ color: primary }}>{selected.name}</p>
                <p className="text-xs text-muted-foreground">{selected.email}</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider mb-2 block">Assine para registrar</Label>
                <SignaturePad onChange={setSig} testid="attendance-signature" />
              </div>
              <div className="text-xs text-muted-foreground flex items-start gap-2">
                <ShieldCheck size={14} className="shrink-0 mt-0.5" />
                <span>Será registrado: IP, navegador, data/hora e assinatura para composição da lista consolidada de presença.</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => { setSelected(null); setSig(""); }} data-testid="cancel-checkin">Voltar</Button>
                <Button style={{ background: primary, color: "#fff" }} onClick={submit} disabled={submitting} data-testid="confirm-checkin">
                  {submitting ? "Registrando…" : "Confirmar presença"}
                </Button>
              </div>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6">{brand.company_name || "Leadera Corp"}</p>
      </div>
    </div>
  );
}
